import React, { useMemo } from 'react';

import { Box, Grid, Group } from '@mantine/core';
import dayjs from 'dayjs';

import { useGetLeadVendorsListQuery } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';

import { LabelAndValue } from '@/types/custom';
import { Lead } from '@/types/matchDevice';
import { DeviceLeadLocation } from '@/types/vendorTransmissionReport';
import DatePickerWrapper from '@/ui/DatePickerWrapper';
import Dropdown from '@/ui/Dropdown';
import IconButton from '@/ui/IconButton';
import TextInputWrapper from '@/ui/TextInputWrapper';
import TextWrapper from '@/ui/TextWrapper';

import RecallBanner from '../../RecallBanner';
import { Sections } from '../../utils/types';

import styles from './LeadsEditModal.module.scss';

interface LeadsEditModalProps {
  leadsData?: Lead[];
  onLeadFieldChange: (leadId: string, field: keyof Lead, value: any) => void;
  onAddLead: (newLead: Lead) => void;
  onDeleteLead: (leadId: string) => void;
  leadStatusErrors: Record<string, string>;
  onLeadStatusError: (leadId: string, error?: string) => void;
  deviceId?: string;
}

const LEAD_LOCATION_OPTIONS: LabelAndValue[] = [
  { label: '-', value: '' },
  { label: 'Apex', value: 'Apex' },
  { label: 'Epicardal', value: 'Epicardal' },
  { label: 'Inflow', value: 'Inflow' },
  { label: 'Lateral Wall', value: 'Lateral Wall' },
  { label: 'Outflow', value: 'Outflow' },
  { label: 'Septum', value: 'Septum' },
  { label: 'Appendage', value: 'Appendage' },
  { label: 'LBB', value: 'LBB' }
];

const LEAD_CAPPED_OPTIONS: LabelAndValue[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }
];

const LEAD_STATUS_OPTIONS: LabelAndValue[] = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' }
];

const CHAMBER_ORDER = ['RA', 'RV', 'LV'] as const;

const valueExists = (value: string | null | undefined): boolean => {
  return value !== null && value !== undefined && value !== '' && value !== '-';
};

const isLeadInactive = (leadStatus?: string): boolean => {
  return leadStatus?.toLowerCase() === 'inactive';
};

const generateTempId = (): string => {
  return `tempid-${Math.random().toString(36).slice(2)}`;
};

const LeadsEditModal: React.FC<LeadsEditModalProps> = ({
  leadsData = [],
  onLeadFieldChange,
  onAddLead,
  onDeleteLead,
  leadStatusErrors,
  onLeadStatusError,
  deviceId
}) => {
  const { data: leadVendorsList } = useGetLeadVendorsListQuery();

  // Group leads by chamber
  const formLeadMap = useMemo(() => {
    const map: Record<DeviceLeadLocation, Lead[]> = { RA: [], RV: [], LV: [] };
    leadsData.forEach((lead) => {
      const loc = lead.leadChamber as DeviceLeadLocation;
      if (map[loc]) {
        map[loc].push(lead);
      }
    });
    return map;
  }, [leadsData]);

  const leadOptions: LabelAndValue[] = useMemo(() => {
    if (!leadVendorsList) return [];
    return leadVendorsList.map((vendor) => ({
      value: vendor.leadVendorId,
      label: vendor.leadVendorName ?? ''
    }));
  }, [leadVendorsList]);

  const handleFieldChange = (fieldName: keyof Lead, value: any, leadId: string) => {
    onLeadFieldChange(leadId, fieldName, value);
  };

  const handleAddLead = (location: DeviceLeadLocation) => {
    const newLead: Lead = {
      id: generateTempId(),
      leadChamber: location,
      leadLocation: undefined,
      implantDate: undefined,
      serialNumber: undefined,
      leadCapped: undefined,
      leadStatus: undefined,
      deviceId: deviceId || '',
      leadModel: undefined,
      leadManufacturer: { leadVendorId: undefined, leadVendorName: undefined }
    };
    onAddLead(newLead);
  };

  const handleImplantDateChange = (date: Date | null, leadId: string) => {
    if (date === undefined) return;
    const dateString = date === null ? '' : dayjs(date).format('MM/DD/YYYY');
    handleFieldChange('implantDate', dateString, leadId);
  };

  const handleLeadStatusChange = (
    option: LabelAndValue | null,
    location: DeviceLeadLocation,
    leadId: string
  ) => {
    if (option?.value?.toLocaleLowerCase() === 'active') {
      const activeLeadsInChamber = formLeadMap[location].filter(
        (lead) => lead.leadStatus?.toLocaleLowerCase() === 'active' && lead.id !== leadId
      );

      if (activeLeadsInChamber.length >= 1) {
        onLeadStatusError(leadId, 'There can be only one active lead in Chamber.');
        setTimeout(() => onLeadStatusError(leadId, undefined), 3000);
        return;
      }
    }

    onLeadStatusError(leadId, undefined);
    onLeadFieldChange(leadId, 'leadStatus', option?.value || null);
  };

  const handleLeadCappedChange = (option: LabelAndValue | null, leadId: string) => {
    const value = option?.value === 'true' ? true : option?.value === 'false' ? false : undefined;
    onLeadFieldChange(leadId, 'leadCapped', value);
  };

  const getLeadCappedValue = (leadCapped?: boolean) => {
    if (leadCapped === null || leadCapped === undefined) return { label: '', value: '' };
    return leadCapped ? { label: 'Yes', value: 'true' } : { label: 'No', value: 'false' };
  };

  const getManufacturerValue = (lead: Lead) => {
    return (
      leadOptions.find((opt) => Number(opt.value) === lead.leadManufacturer?.leadVendorId) || {
        label: '',
        value: ''
      }
    );
  };

  const getLeadLocationValue = (leadLocation?: string | null) => {
    return (
      LEAD_LOCATION_OPTIONS.find((opt) => opt.value === leadLocation) || LEAD_LOCATION_OPTIONS[0]
    );
  };

  const renderLeadFields = (lead: Lead, location: DeviceLeadLocation) => {
    const isInactive = isLeadInactive(lead.leadStatus);
    return (
      <>
        <Grid align="center" className={styles.leadFieldsGrid}>
          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <Dropdown
              label="Manufacturer"
              options={leadOptions}
              value={getManufacturerValue(lead)}
              labelTextProps={{ variant: 'regular' }}
              onChange={(option) =>
                handleFieldChange(
                  'leadManufacturer',
                  option
                    ? {
                        leadVendorId: option.value,
                        leadVendorName: option.label
                      }
                    : null,
                  lead.id ?? ''
                )
              }
              placeholder="Select Manufacturer"
              isDisabled={isInactive}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <TextInputWrapper
              label="Model"
              value={valueExists(lead.leadModel) ? lead.leadModel : ''}
              onChange={(e) => handleFieldChange('leadModel', e.target.value, lead.id ?? '')}
              disabled={isInactive}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <DatePickerWrapper
              inputProps={{
                label: 'Implant Date',
                disabled: isInactive
              }}
              datePickerProps={{
                id: 'date',
                selected:
                  lead.implantDate && lead.implantDate !== '-'
                    ? dayjs(lead.implantDate).toDate()
                    : null,
                onChange: (date: Date | null) => handleImplantDateChange(date, lead.id ?? ''),
                isClearable: true,
                disabled: isInactive
              }}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <TextInputWrapper
              label="Serial Number"
              value={valueExists(lead.serialNumber) ? lead.serialNumber : ''}
              onChange={(e) => handleFieldChange('serialNumber', e.target.value, lead.id ?? '')}
              disabled={isInactive}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <Dropdown
              label="Lead Location"
              options={LEAD_LOCATION_OPTIONS}
              value={getLeadLocationValue(lead.leadLocation)}
              labelTextProps={{ variant: 'regular' }}
              onChange={(option) => handleFieldChange('leadLocation', option?.value, lead.id ?? '')}
              placeholder="Select Lead Location"
              isDisabled={isInactive}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <Dropdown
              label="Lead Capped"
              options={LEAD_CAPPED_OPTIONS}
              value={getLeadCappedValue(lead.leadCapped)}
              labelTextProps={{ variant: 'regular' }}
              onChange={(option) => handleLeadCappedChange(option, lead.id ?? '')}
              placeholder="Lead Capped"
              isDisabled={isInactive}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
            <Dropdown
              label="Lead Status"
              options={LEAD_STATUS_OPTIONS}
              value={{
                label: lead.leadStatus ?? '',
                value: lead.leadStatus ?? ''
              }}
              labelTextProps={{ variant: 'regular' }}
              onChange={(option) => handleLeadStatusChange(option, location, lead.id ?? '')}
              placeholder="Lead Status"
            />
            {leadStatusErrors[lead.id ?? ''] && (
              <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                {leadStatusErrors[lead.id ?? '']}
              </div>
            )}
          </Grid.Col>
        </Grid>
        {lead?.recall && <RecallBanner deviceRecallData={{ ...lead.recall, location } as any} />}
      </>
    );
  };

  const handleDeleteLead = (lead: Lead, location: DeviceLeadLocation) => {
    const leadsInChamber = formLeadMap[location];
    if (leadsInChamber.length === 1) {
      // Clear fields if only one lead in chamber
      onDeleteLead(lead.id ?? '');
      handleAddLead(location);
    } else {
      onDeleteLead(lead.id ?? '');
    }
  };

  const renderLeadSection = (leads: Lead[], location: DeviceLeadLocation) => {
    if (!leads.length) return null;

    const allInactive = leads.every((lead) => isLeadInactive(lead.leadStatus));

    return (
      <div className={styles.leadSectionGroup} key={location}>
        <Group justify="space-between" align="center" className={styles.leadSectionHeader}>
          <TextWrapper text={Sections[location]} variant="heading" />
        </Group>

        {leads.map((lead, index) => (
          <>
            {index > 0 ? <div className={styles.leadDivider} /> : null}
            <div className={styles.leadSection} key={lead.id}>
              <Group justify="flex-end" align="center">
                <Box>
                  {index === 0 && allInactive && (
                    <IconButton
                      icon="plus"
                      label="Add Lead"
                      className={styles.addLeadButton}
                      onClick={() => handleAddLead(location)}
                    />
                  )}
                  <IconButton
                    icon="trash"
                    label="Delete"
                    className={styles.deleteButton}
                    onClick={() => handleDeleteLead(lead, location)}
                  />
                </Box>
              </Group>
              {renderLeadFields(lead, location)}
            </div>
          </>
        ))}
      </div>
    );
  };

  return (
    <div>
      {CHAMBER_ORDER.map((chamber) =>
        renderLeadSection(formLeadMap[chamber as DeviceLeadLocation], chamber as DeviceLeadLocation)
      )}
    </div>
  );
};

export default LeadsEditModal;
